import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowLeftOutlined, InfoCircleOutlined, LoadingOutlined, LockOutlined } from "@ant-design/icons";
import { Button, Spin, Typography, theme } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef } from "react";
import { PaddleFrame } from "./PaddleFrame.js";
import { useStores } from "../../../../store/stores.js";
import { __, _i } from "../../../../utils/i18n.js";
import { getLegalManufacturerUrls } from "../../../../utils/legalEntities.js";
import { CheckoutStep } from "../../store/CheckoutV2Store.js";
const externalLinkProps = {
    target: "_blank",
    rel: "noopener noreferrer"
};
/**
 * Payment step. Pre-submit: just a compact payment-methods placeholder so
 * nothing competes with the form above. Post-submit: section title, back-
 * link, trust pill, Paddle iframe, cancellation note — the parent has
 * unmounted the form so this renders solo.
 */ const PaymentSection = observer(()=>{
    const { checkoutV2Store, optionStore } = useStores();
    const { transaction, customerCenterUrl, step, product } = checkoutV2Store;
    const { token } = theme.useToken();
    const manufacturer = getLegalManufacturerUrls();
    const hasIdentification = !!transaction?.identification;
    // Paddle's inline iframe flashes its own "Transaction completed"
    // screen between our `Checkout.PaymentComplete` handler and the
    // redirect to SuccessView. Once the transaction is in
    // PaymentProcessing the backend poll is already in flight — hide
    // the iframe and show a plain brand-coloured spinner. No text or
    // image: the buyer expects "a moment until this page changes" and
    // a spinner communicates exactly that.
    const isPaymentProcessing = step >= CheckoutStep.PaymentProcessing;
    // The cancellation wording only applies to recurring products; for
    // one-off purchases there is nothing to cancel, so hide it entirely.
    // Also suppress it during PaymentProcessing so the spinner sits on
    // its own without a trailing disclaimer that is about to disappear
    // with the SuccessView redirect anyway.
    const showCancellationNote = !!product && !product.isOneTime && !isPaymentProcessing;
    const paymentMethodsUrl = `${optionStore.publicUrl}images/payment-methods.png`;
    // Drop the incomplete-checkout so the form re-mounts in edit mode. The
    // user's prior values stay restored via useRecoverableFormV2's `localStorage` cache.
    const handleEditDetails = useCallback(()=>{
        transaction?.resetIdentification();
    }, [
        transaction
    ]);
    // Post-submit the form unmounts and Payment renders at the top of the
    // left column — pull the buyer up to the new heading, but only if it is
    // out of view so we never override an intentional scroll position.
    const headingRef = useRef(null);
    useEffect(()=>{
        if (!hasIdentification) {
            return;
        }
        const el = headingRef.current;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const stickyStepperBand = 88;
        const isVisible = rect.top >= stickyStepperBand && rect.top < window.innerHeight;
        if (!isVisible) {
            el.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        }
        // Focus the Payment heading so a keyboard/screen-reader user is taken
        // from the just-submitted form onto the new active section — without
        // this the focus stays on the now-unmounted submit button, which
        // lands focus back at `<body>` silently.
        el.focus({
            preventScroll: true
        });
    }, [
        hasIdentification
    ]);
    // When the Paddle iframe is swapped for the processing spinner, the
    // buyer is typically scrolled deep into where the iframe's payment
    // form used to sit — on mobile especially, the spinner then lands
    // above the viewport and looks like "nothing happened" for the few
    // seconds until the success view redirect. Centering the spinner
    // pulls the page back up and leaves natural headroom above it.
    const spinnerRef = useRef(null);
    useEffect(()=>{
        if (!isPaymentProcessing) {
            return;
        }
        const el = spinnerRef.current;
        if (!el) {
            return;
        }
        el.scrollIntoView({
            behavior: "smooth",
            block: "center"
        });
    }, [
        isPaymentProcessing
    ]);
    if (!hasIdentification) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "checkout-v2-payment-placeholder",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "checkout-v2-payment-placeholder__hint",
                    children: __("Pay with PayPal, credit card, and more.")
                }),
                /*#__PURE__*/ _jsx("img", {
                    src: paymentMethodsUrl,
                    alt: __("Accepted payment methods"),
                    className: "checkout-v2-payment-placeholder__methods"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: headingRef,
                // `tabIndex={-1}` — programmatically focusable (post-submit effect) but skipped by Tab.
                tabIndex: -1,
                className: "checkout-v2-payment-heading",
                style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 16,
                    gap: 12,
                    flexWrap: "wrap"
                },
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Typography.Title, {
                                level: 5,
                                className: "checkout-v2-section-heading",
                                style: {
                                    margin: 0
                                },
                                children: __("Payment")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "checkout-v2-payment-trust",
                                style: {
                                    color: token.colorTextHeading
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(LockOutlined, {
                                        "aria-hidden": true,
                                        style: {
                                            color: token.colorSuccess
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: __("Secure, encrypted payment via Paddle")
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "link",
                        size: "small",
                        icon: /*#__PURE__*/ _jsx(ArrowLeftOutlined, {}),
                        onClick: handleEditDetails,
                        style: {
                            padding: 0,
                            fontSize: 13
                        },
                        children: __("Edit details")
                    })
                ]
            }),
            isPaymentProcessing && /*#__PURE__*/ _jsx("div", {
                ref: spinnerRef,
                className: "checkout-v2-payment-processing",
                role: "status",
                "aria-label": __("Processing your payment"),
                children: /*#__PURE__*/ _jsx(Spin, {
                    indicator: /*#__PURE__*/ _jsx(LoadingOutlined, {
                        spin: true,
                        style: {
                            fontSize: 48
                        }
                    })
                })
            }),
            /*#__PURE__*/ _jsx(PaddleFrame, {
                style: isPaymentProcessing ? {
                    display: "none"
                } : undefined
            }),
            showCancellationNote && /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-cancellation-note",
                style: {
                    color: token.colorTextSecondary
                },
                children: [
                    /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: _i(__("Your subscription renews automatically unless canceled. {{strong}}Cancel anytime{{/strong}} prior to your renewal date in the {{customerCenter}}customer center{{/customerCenter}} or by contacting our {{support}}support{{/support}}."), {
                            strong: /*#__PURE__*/ _jsx("strong", {}),
                            customerCenter: customerCenterUrl ? /*#__PURE__*/ _jsx("a", {
                                href: customerCenterUrl,
                                ...externalLinkProps
                            }) : /*#__PURE__*/ _jsx("span", {}),
                            support: /*#__PURE__*/ _jsx("a", {
                                href: manufacturer.support,
                                ...externalLinkProps
                            })
                        })
                    })
                ]
            })
        ]
    });
});
export { PaymentSection };
